import { Link } from 'react-router-dom';
import {
  BookUser,
  CalendarDays,
  ChevronLeft,
  Gauge,
  Shirt,
  Users,
  UsersRound,
  Wallet,
} from 'lucide-react';

/**
 * Le sommaire du registre des adhérents, sur téléphone.
 *
 * POURQUOI CET ÉCRAN N'EXISTE QUE SUR TÉLÉPHONE
 * ---------------------------------------------------------------------------
 * Sur ordinateur, les six écrans du registre sont dépliés en permanence dans
 * la barre de gauche : un sommaire n'apprendrait rien et ajouterait un clic.
 * La carte « Adhérents » y mène donc droit au répertoire.
 *
 * Sur téléphone, il n'y a pas de barre latérale. La carte menait au
 * répertoire, et les cinq autres écrans — le tableau du club, les cotisations,
 * les équipements, les équipes, le calendrier — n'étaient atteignables qu'en
 * naviguant depuis lui. Le registre est pourtant ce qu'on ouvre le plus
 * souvent en déplacement : au bord d'un terrain, dans un vestiaire, devant un
 * parent qui demande si la licence est passée.
 *
 * DEUX COLONNES, ET C'EST UNE MESURE
 * ---------------------------------------------------------------------------
 * Trois cartes par ligne sur un écran de 390 pixels donnent des cibles de
 * 110 pixels avec leur libellé sur deux lignes. Deux colonnes laissent
 * 170 pixels : le pouce ne rate pas, et « Licences et cotisations » tient.
 *
 * L'ORDRE EST CELUI DU TRAVAIL, PAS L'ALPHABET
 * ---------------------------------------------------------------------------
 * Le tableau d'abord : il répond à « y a-t-il quelque chose à faire » sans
 * qu'on ait à visiter les cinq autres. Puis le répertoire, puis l'argent,
 * puis le matériel, et enfin les équipes et leur calendrier — on compose un
 * groupe avant de savoir contre qui il joue.
 */

const ECRANS = [
  {
    to: '/adherents/tableau-de-bord',
    label: 'Où en est le club',
    detail: 'Ce qui attend, d\'un coup d\'œil',
    icone: Gauge,
  },
  {
    to: '/adherents',
    label: 'Répertoire',
    detail: 'Les fiches, les familles',
    icone: BookUser,
  },
  {
    to: '/adherents/cotisations',
    label: 'Licences et cotisations',
    detail: 'Ce qui est réglé, ce qui traîne',
    icone: Wallet,
  },
  {
    to: '/adherents/equipements',
    label: 'Équipements',
    detail: 'Ce qui est prêté, à qui',
    icone: Shirt,
  },
  {
    to: '/adherents/equipes',
    label: 'Équipes',
    detail: 'Qui joue ensemble',
    icone: Users,
  },
  {
    to: '/adherents/rencontres',
    label: 'Calendrier',
    detail: 'Contre qui, et quand',
    icone: CalendarDays,
  },
];

export default function AdherentsAcces() {
  return (
    <div className="p-4 pb-24 space-y-4">
      <div>
        {/*
          Le retour est explicite : cet écran s'ouvre depuis l'accès rapide, et
          le geste système de retour n'existe pas sur une application installée
          en plein écran sur iOS.
        */}
        <Link
          to="/acces-rapide"
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-700"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          Accès rapide
        </Link>

        <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
          <UsersRound className="w-6 h-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          Adhérents
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Le registre du club : qui est licencié, ce qui est réglé, ce qui est prêté, et qui joue
          quand.
        </p>
      </div>

      {/* Deux colonnes, trois lignes. Voir l'en-tête : c'est une mesure, pas
          une préférence. */}
      <div className="grid grid-cols-2 gap-3">
        {ECRANS.map((ecran) => (
          <Link
            key={ecran.to}
            to={ecran.to}
            className="card flex flex-col items-center justify-center gap-2 py-5 px-3 text-center hover:shadow-md transition"
          >
            <ecran.icone className="w-7 h-7 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <span className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">
              {ecran.label}
            </span>
            <span className="text-[0.7rem] leading-snug text-gray-500 dark:text-gray-400">
              {ecran.detail}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
